// Browser Sentry loads after the page is interactive so the SDK never delays
// first render. Until then, uncaught errors are buffered and reported when it
// initializes; UI calls go through @/lib/sentry-browser, which queues them.

import { sentryUnavailable } from "@/lib/sentry-browser";
import { bufferBrowserError, type BufferedBrowserError } from "@/lib/browser-error-diagnostics";

const earlyErrors: BufferedBrowserError[] = [];
const bufferError = (event: ErrorEvent) => earlyErrors.push(bufferBrowserError(event));
const bufferRejection = (event: PromiseRejectionEvent) => earlyErrors.push({
  error: event.reason,
  mechanism: "auto.browser.global_handlers.onunhandledrejection",
});

function stopBuffering() {
  window.removeEventListener("error", bufferError);
  window.removeEventListener("unhandledrejection", bufferRejection);
}

function loadSentry() {
  void import("@/lib/sentry-client").then(
    ({ startSentry }) => {
      stopBuffering();
      startSentry(earlyErrors.splice(0));
    },
    () => {
      // A missing chunk after a deploy must not interrupt the page.
      stopBuffering();
      sentryUnavailable();
    },
  );
}

function scheduleWhenIdle() {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadSentry, { timeout: 2_000 });
  } else {
    globalThis.setTimeout(loadSentry, 0);
  }
}

window.addEventListener("error", bufferError);
window.addEventListener("unhandledrejection", bufferRejection);
if (document.readyState === "complete") scheduleWhenIdle();
else window.addEventListener("load", scheduleWhenIdle, { once: true });
